import { useMemo } from "react";
import type { ChangeEvent } from "react";

import { UpdateModal } from "@components/Page";
import { MODAL_WIDTH2 } from "@components/Constants";
import { getIsoDateFromStringDate } from "@components/Date";
import { useForm, useWatch, TYPE_DATE, TYPE_NONE } from "@components/Form";
import { updateInvestmentAccreditationOperation as operation } from "@api/services/investments";

import type { ModalRef, UpdateInvestment } from "../../types";

const LABEL_SAVE = "Save";
const LABEL_ENTITY = "Accreditation";
const LABEL_CHANGE_EMAIL = "Edit Investment Accreditation";

type Parameters = {
  mutation: {
    hasAccreditation?: boolean;
    accreditationExpiresAt?: string;
  };
};

type Props = {
  modal: ModalRef;
  onSuccess: UpdateInvestment;
};

const InvestmentAccreditationUpdateModal = ({
  modal,
  onSuccess,
}: Props) => {
  const form = useForm();
  const hasAccreditation = useWatch("hasAccreditation", form);

  const schema = useMemo(() =>
    operation.getSchema([
      {
        name: "hasAccreditation",
        label: "Is an accredited investor?",
        required: false,
        inputProps: {
          onChange: ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
            if (!value) {
              form.setFieldValue([ "accreditationExpiresAt" ], undefined);
            }
          },
        },
      },
      {
        name: "accreditationExpiresAt",
        label: "Accreditation Expiration Date",
        required: false,
        type: hasAccreditation
          ? TYPE_DATE
          : TYPE_NONE,
      },
    ])
  , [ form, hasAccreditation ]);

  const onSubmit = (parameters: Parameters) => {
    const { mutation } = parameters;
    const { accreditationExpiresAt } = mutation;

    if (accreditationExpiresAt) {
      const isoDate = getIsoDateFromStringDate(accreditationExpiresAt);
      parameters.mutation.accreditationExpiresAt = `${isoDate}T00:00:00Z`;
      parameters.mutation.hasAccreditation = true;
    }

    return parameters;
  };

  const updateModalProps = {
    width: MODAL_WIDTH2,
    title: LABEL_CHANGE_EMAIL,
    entity: LABEL_ENTITY,
    submitLabel: LABEL_SAVE,
    shouldOptimizeUpdateMutation: false,
    form,
    modal,
    schema,
    onSubmit,
    onSuccess,
    operation,
  };

  return (
    <UpdateModal
      {...updateModalProps}
    />
  );
};

export default InvestmentAccreditationUpdateModal;
